// The address bar, in both directions.
//
// Reading: a URL drives state (on load, and on every Back/Forward). Writing: a
// state change updates the bar. The rules about WHICH parts belong in the bar
// are pure and live in lib/route-state.ts, with tests; this module is the
// imperative half — history, fetch, and the map moves a route implies.
//
// Three orderings here are load-bearing and each has a named test in
// lib/route-state.test.ts backing the rule it enforces:
//
//   1. `applyingRoute` is released BEFORE openCity, so openCity's own persist()
//      writes the city URL rather than being muted by the flag that protected
//      the deep link on the way in.
//   2. Camera addressing RESETS on every route apply (flagsForIncomingRoute) and
//      is set back to true only for a coord route — a restored camera is a
//      committed one, but an inherited flag would make a country page claim a
//      camera it never had.
//   3. markUserCamera ignores route-driven moves, so the fitBounds that frames a
//      country is not mistaken for the reader panning away from it.

import { map } from '../map/instance.ts';
import { state } from '../state.ts';
import { getCountries } from '../persist.ts';
import type { CountryRow } from '../persist.ts';
import { openCity } from '../city/panel.ts';
import { setReplaying, trackPageview } from '../analytics.ts';
import { setView } from '../ui/layout.ts';
import { unlockCustom } from '../ui/chrome.ts';
import { parseRoute, keyForDateSlug, varForModeSlug, parseDayRoute, parseCompareRoute } from '../../lib/slug.ts';
import { applyDayFromRoute } from '../myday.ts';
import { openCompare, placeFrom } from '../compare/view.ts';
import {
  routePathFor, routeKeyFor, flagsForIncomingRoute, isHistoryPush, roundCamera, NEUTRAL_FLAGS,
} from '../../lib/route-state.ts';
import type { RouteFlags, RouteInputs } from '../../lib/route-state.ts';
import { decodeRecipe } from '../../lib/recipe.ts';
import type { RecipeInput } from '../../lib/recipe.ts';
import { VAR } from '../../lib/variables.ts';
import type { VarKey } from '../../lib/variables.ts';
import type { CityHit } from '../../contracts/city.ts';

// The mutators a route has to drive. Injected because applying a URL is the app
// changing its own mind, not the routing layer knowing how to repaint a field.
export interface UrlSyncDeps {
  setRecipe: (r: RecipeInput | null | undefined, opts?: { defer?: boolean }) => void;
  applyVariable: (v: VarKey, opts?: { explicit?: boolean }) => void;
  showFrame: (idx: number, opts?: { explicit?: boolean }) => void;
}

let deps: UrlSyncDeps;

export function initUrlSync(d: UrlSyncDeps): void {
  deps = d;
}

let applyingRoute = false; // true while we drive state FROM the URL — mutes the writer
let lastSyncKey: string | null = null; // screen identity of the last write, to choose push vs replace
// The four neutrality/addressing flags, as one object. What each means and how it
// gates a slug is documented in lib/route-state.ts.
let routeFlags: RouteFlags = { ...NEUTRAL_FLAGS };

// The state the URL is a function of. The camera is read here because only the
// map knows it, and only when the flags say it could matter.
function routeInputs(): RouteInputs {
  const c = routeFlags.cameraTouched && !routeFlags.countryFocus && state.view === 'map' ? map.getCenter() : null;
  const seed = state.daySeed;
  return {
    view: state.view,
    city: state.selectedCity,
    dateKey: state.dates[state.dateIdx],
    variable: state.variable,
    recipeCanon: state.recipeCanon,
    camera: c ? roundCamera(c.lat, c.lng, map.getZoom()) : null,
    flags: routeFlags,
    day: seed && seed.placeSlug ? { place: seed.placeSlug, dateKey: seed.dekad, bucket: seed.bucket, edited: state.recipeCanon !== seed.canon } : null,
    compare: state.view === 'compare' && state.compare.places.every((p) => p.placeSlug)
      ? { places: state.compare.places.map((p) => p.placeSlug), full: state.compare.full }
      : null,
  };
}

// Resolve a country-free place slug (/day/, /compare/) to a city.
async function fetchPlace(slug: string): Promise<CityHit | null> {
  try {
    const res = await fetch(`/api/place/${encodeURIComponent(slug)}`);
    return res.ok ? ((await res.json()) as CityHit) : null;
  } catch {
    return null;
  }
}

// The listeners this module answers to: which gestures promote the URL to an
// exact camera, and Back/Forward re-deriving the whole view from the path.
export function wireHistory() {
  // A reader-driven camera move promotes the map URL from a bare/country path to
  // the exact @lat,lng,zoom camera. Detected at the START of a gesture: dragstart
  // is always a user (no programmatic drag exists), and zoom/rotate/pitch carry an
  // originalEvent only for real input — a fitBounds/flyTo easing does not. moveend
  // is unreliable here (inertia tails fire without originalEvent).
  map.on('dragstart', markUserCamera);
  for (const ev of ['zoomstart', 'rotatestart', 'pitchstart']) {
    map.on(ev, (e) => { if (e && e.originalEvent) markUserCamera(); });
  }

  // Back/Forward: re-derive the whole view from the path the browser restored,
  // gliding the camera to it rather than snapping.
  window.addEventListener('popstate', () => { applyRouteFromUrl({ animate: true, fromPopstate: true }); });
}

let syncTimer: ReturnType<typeof setTimeout> | null = null;

// Write the address bar NOW. Reads the live camera/state, so callers schedule it
// (below) rather than call it mid-transition.
function writeUrl() {
  syncTimer = null;
  if (applyingRoute) return;
  const i = routeInputs();
  const path = routePathFor(i);
  const key = routeKeyFor(i, state.selectedCity ? state.selectedCity.id : null);
  if (path === location.pathname + location.search) { lastSyncKey = key; return; }
  try {
    if (isHistoryPush(lastSyncKey, key)) history.pushState(null, '', path);
    else history.replaceState(null, '', path);
  } catch {
    /* history may be unavailable (sandboxed frame) — URL sync is best-effort */
  }
  lastSyncKey = key;
}

// Debounced/trailing: a view switch fires persist() and several resize-driven
// moveends inside the ~300ms transition; writing history synchronously in each
// hitched the animation. Coalesce them into one write after the motion settles.
export function syncUrl() {
  if (applyingRoute) return;
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(writeUrl, 350);
}

// Drive state from the current path. Called once at startup (animate:false, an
// instant jump so there is no opening fly) and on every Back/Forward
// (animate:true, so the restore glides rather than snaps).
export async function applyRouteFromUrl({ animate = false, fromPopstate = false }: { animate?: boolean; fromPopstate?: boolean } = {}) {
  const parts = parseRoute(location.pathname);
  applyingRoute = true;
  // Analytics keeps its own flag rather than reading applyingRoute, which is
  // cleared early below so openCity can write the city URL. A route landing is
  // one page view, not the burst of interactions replaying it looks like.
  setReplaying(true);
  // Re-derived on every read, not just on user action — that is what makes
  // Back/Forward symmetric (see flagsForIncomingRoute).
  routeFlags = flagsForIncomingRoute(parts);
  try {
    // /day/{place}/{date}/{bucket}/ — a rating authored from a day. The day
    // rebuilds the recipe; a ?r= alongside it is an edit made since, applied on
    // top. Resolved before anything else: under the map grammar "day" would
    // read as a country.
    const day = parseDayRoute(location.pathname);
    if (day) {
      const hit = await fetchPlace(day.place);
      if (hit) {
        applyingRoute = false; // applyDay's own persist() writes the day's URL
        const ok = await applyDayFromRoute(hit, day.dateSlug ? keyForDateSlug(day.dateSlug) : null, day.bucket);
        const edited = decodeRecipe(new URLSearchParams(location.search).get('r'));
        if (ok && edited) deps.setRecipe(edited);
        return;
      }
      if (fromPopstate) setView('landing');
      return;
    }
    // /compare/{a}/{b}[/{c}][/{date}][/{mode}][/full]/ — the places, then the
    // same date and rating slugs every other page takes.
    const cmp = parseCompareRoute(location.pathname);
    if (cmp) {
      const shared = decodeRecipe(new URLSearchParams(location.search).get('r'));
      if (shared) { unlockCustom(); deps.setRecipe(shared); }
      if (cmp.modeSlug) {
        const v = varForModeSlug(cmp.modeSlug);
        if (v && VAR[v]) deps.applyVariable(v);
      }
      if (cmp.dateSlug) {
        const key = keyForDateSlug(cmp.dateSlug);
        const idx = key ? state.dates.indexOf(key) : -1;
        if (idx >= 0) deps.showFrame(idx);
      }
      const hits = (await Promise.all(cmp.places.map(fetchPlace))).filter((h): h is CityHit => !!h);
      if (hits.length >= 2) {
        applyingRoute = false;
        await openCompare(hits.map(placeFrom), { full: cmp.full, animate });
        return;
      }
      if (fromPopstate) setView('landing');
      return;
    }
    // A shared recipe applies BEFORE the mode slug selects "My kind of day", so the
    // custom field's first tile fetch already carries it. Arriving on a shared
    // link IS seeing the feature — unlock it even if the link's mode slug is
    // something else.
    const shared = decodeRecipe(new URLSearchParams(location.search).get('r'));
    if (shared) {
      unlockCustom();
      deps.setRecipe(shared);
    }
    if (parts.modeSlug) {
      const v = varForModeSlug(parts.modeSlug);
      if (v && VAR[v]) deps.applyVariable(v);
    }
    if (parts.dateSlug) {
      const key = keyForDateSlug(parts.dateSlug);
      const idx = key ? state.dates.indexOf(key) : -1;
      if (idx >= 0) deps.showFrame(idx);
    }
    if (parts.countrySlug && parts.citySlug) {
      try {
        const res = await fetch(`/api/loc/${encodeURIComponent(parts.countrySlug)}/${encodeURIComponent(parts.citySlug)}`);
        if (res.ok) {
          const c = (await res.json()) as CityHit;
          applyingRoute = false; // let openCity's own persist() write the city URL
          // animate mirrors every other branch here: a first load lands on the
          // city outright, a Back/Forward glides to it.
          await openCity(c.id, { name: c.name, nameAscii: c.nameAscii, country: c.country, admin: c.admin, citySlug: c.citySlug, pop: c.pop, lon: c.lon, lat: c.lat }, { animate });
          return;
        }
        // A country/city pair that names no city (a stale or mistyped city) falls
        // through to the country branch below, so the reader still lands on the
        // country rather than the bare landing.
      } catch {
        /* fall through — leave the default view */
      }
    }
    // Country-only: frame the map on the country and hold /{country}/ in the bar
    // until the reader moves. Also the fallthrough for an unresolved city above.
    if (parts.countrySlug) {
      const c = (await getCountries()).find((x: CountryRow) => x.slug === parts.countrySlug);
      if (c && Array.isArray(c.bbox) && c.bbox.length === 4) {
        setView('map');
        routeFlags.countryFocus = c.slug;
        routeFlags.cameraTouched = false;
        const [w, s, e, n] = c.bbox;
        // maxZoom guards a single-city country whose bbox is a point — fitBounds
        // would otherwise slam to max zoom. routeDriven tags the move as
        // programmatic so the camera listeners don't read it as a user pan.
        map.fitBounds([[w, s], [e, n]], { padding: 48, maxZoom: 8, duration: animate ? 600 : 0 }, { routeDriven: true });
        return;
      }
      // Unknown country slug → fall through to the default view, URL left alone.
    }
    if (parts.coord) {
      setView('map');
      routeFlags.cameraTouched = true; // a shared/restored camera is a committed one
      const { lat, lng, zoom } = parts.coord;
      if (animate) map.flyTo({ center: [lng, lat], zoom, duration: 600 });
      else map.jumpTo({ center: [lng, lat], zoom });
      return;
    }
    // No usable route. On Back/Forward the bare "/" must mean the landing search
    // (its history entry was pushed from there); on first load we leave main()'s
    // restored default (landing, or the saved map view) untouched.
    if (fromPopstate) setView('landing');
  } finally {
    applyingRoute = false;
    // The route has landed, whichever branch above landed it — so this is where
    // the reader is, and the one page view worth counting for it. Boot and every
    // Back/Forward pass through here exactly once.
    setReplaying(false);
    trackPageview();
    lastSyncKey = null; // re-seed so the first user action after this computes push/replace correctly
    // Drop any write scheduled before this navigation — a load/restore must never
    // rewrite the bar. (During startup nothing is scheduled; on popstate this
    // cancels a debounced write of the pre-navigation state.)
    if (syncTimer) clearTimeout(syncTimer);
  }
}

// The reader has committed a camera worth naming: from here the map URL is the
// exact @lat,lng,zoom, not a bare or country path. Clears any country framing so
// panning away from /{country}/ leaves it.
export function markUserCamera() {
  if (applyingRoute) return; // a route-driven fitBounds/flyTo is not a user move
  routeFlags.cameraTouched = true;
  routeFlags.countryFocus = null;
}

// Mute the writer through a block of programmatic state changes — main()'s
// startup seeding, which would otherwise rewrite the bar to "/" and clobber the
// deep link before applyRouteFromUrl gets to read it.
export function setApplyingRoute(on: boolean): void {
  applyingRoute = on;
}

// A user action has named the date or the field, so the slug belongs in the bar.
export function markDateExplicit(): void {
  routeFlags.dateExplicit = true;
}

export function markModeExplicit(): void {
  routeFlags.modeExplicit = true;
}
